import React, { useState } from 'react';
import { Card } from '../../SubscriptionAppCard';
import { Button } from '../../SubscriptionAppButton';
import { CheckCircle, Mail, MessageSquare, CreditCard, Package, RefreshCw, Calendar, Filter, ClipboardList, Plus, Phone, Store } from 'lucide-react';
import { FilterPill } from '../FilterPill';
import { NoteInput } from '../NoteInput';
import { TimelineEvent } from '../TimelineEvent';
import { EmptyState } from '../EmptyState';
export interface Event {
  id: number;
  type: string;
  date: string;
  title: string;
  description: string;
  author?: string;
  taskMeta?: {
    assignee?: string;
    dueDate?: string;
    priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
    status?: 'Open' | 'In Progress' | 'Complete';
    linkedEntity?: string;
    isOverdue?: boolean;
  };
}
export interface ActivityTimelineProps {
  /** Array of events to display in the timeline */
  events: Event[];
  /** Callback when a new note is added */
  onAddNote?: (note: string) => void;
  /** Callback when a new task is added */
  onAddTask?: () => void;
  /** Filter applied on first render (e.g. 'note'); filters stay switchable */
  defaultFilter?: string;
  /** Optional data-id for component identification */
  'data-id'?: string;
}
const eventTypeConfig: Record<string, {
  label: string;
  icon: any;
  color: string;
}> = {
  payment_success: {
    label: 'Payments',
    icon: CheckCircle,
    color: '#10B981'
  },
  product_swap: {
    label: 'Product Swaps',
    icon: RefreshCw,
    color: '#0EA5E9'
  },
  box_change: {
    label: 'Box Changes',
    icon: Package,
    color: '#6F42C1'
  },
  payment_method_updated: {
    label: 'Payment Updates',
    icon: CreditCard,
    color: '#F59E0B'
  },
  cycle_skipped: {
    label: 'Skipped Cycles',
    icon: Calendar,
    color: '#EF4444'
  },
  email_sent: {
    label: 'Emails',
    icon: Mail,
    color: '#3B82F6'
  },
  note: {
    label: 'Notes',
    icon: MessageSquare,
    color: '#64748B'
  },
  task: {
    label: 'Tasks',
    icon: ClipboardList,
    color: '#8B5CF6'
  },
  call: {
    label: 'Calls',
    icon: Phone,
    color: '#0EA5E9'
  },
  cellar_door_visit: {
    label: 'Cellar door visits',
    icon: Store,
    color: '#10B981'
  }
};
export function ActivityTimeline({
  events,
  onAddNote,
  onAddTask,
  defaultFilter = 'all',
  'data-id': dataId
}: ActivityTimelineProps) {
  const [activeFilter, setActiveFilter] = useState<string>(defaultFilter);
  // When starting filtered, show the pills so it's clear how to see everything
  const [showFilters, setShowFilters] = useState(defaultFilter !== 'all');
  const filteredEvents = activeFilter === 'all' ? events : events.filter(event => event.type === activeFilter);
  const getEventIcon = (type: string) => {
    const config = eventTypeConfig[type];
    if (!config) return MessageSquare;
    return config.icon;
  };
  const getEventColor = (type: string) => {
    const config = eventTypeConfig[type];
    return config?.color || '#64748B';
  };
  const filterOptions = [{
    value: 'all',
    label: 'All Activity'
  }, ...Object.entries(eventTypeConfig).map(([key, config]) => ({
    value: key,
    label: config.label
  }))];
  return <Card padding="none" data-id="element-4795" data-mp="293ztfs6nh0ba">
      {/* Header with Filter Button */}
      <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-4796" data-mp="2926n6dzy8i3t">
        <div className="flex justify-between items-center" data-id="element-4797" data-mp="2432h3w8bn0hp">
          <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-4798" data-mp="1z74x9fxunc06">
            Activity & Notes
          </h3>
          <div className="flex gap-2" data-id="element-4799" data-mp="1v3p1eltltfoj">
            {onAddTask && <Button variant="outline" size="md" onClick={onAddTask} iconLeft={<Plus className="w-4 h-4" data-id="element-4801" data-mp="1q5nji47wzmen" />} data-id="element-4800" data-mp="1q55gmoqqy9xr">
                Add Task
              </Button>}
            <Button variant="outline" size="md" onClick={() => setShowFilters(!showFilters)} iconLeft={<Filter className="w-4 h-4" data-id="element-4803" data-mp="3kiwnjghoyfpi" />} aria-expanded={showFilters} aria-label="Toggle activity filters" data-id="element-4802" data-mp="1l85bcg1q1b7p">
              Filter
            </Button>
          </div>
        </div>

        {/* Filter Pills */}
        {showFilters && <div className="flex flex-wrap gap-2 mt-4" role="group" aria-label="Activity filters" data-id="element-4804" data-mp="2d52z4653x9x7">
            {filterOptions.map(option => <FilterPill key={option.value} label={option.label} value={option.value} isActive={activeFilter === option.value} onClick={setActiveFilter} data-id="element-4805" data-mp="238zvevulx385" />)}
          </div>}
      </div>

      {/* Note Input */}
      {onAddNote && <div className="p-6 border-b border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-4806" data-mp="1thvjy64540i2">
          <NoteInput onSubmit={onAddNote} data-id="element-4807" data-mp="1y9en3utjriir" />
        </div>}

      {/* Timeline Feed */}
      <div className="max-h-[400px] overflow-y-auto p-6 space-y-6 bg-white" data-id="element-4808" data-mp="1ybhplebvz44d">
        {filteredEvents.length === 0 ? <EmptyState title="No events match the selected filter" data-id="element-4809" data-mp="1phcqy8qigoea" /> : <ol className="list-none" role="list" aria-label="Activity timeline" data-id="element-4810" data-mp="1phuxby6w0izx">
            {filteredEvents.map((event, index) => <TimelineEvent key={event.id} title={event.title} description={event.description} date={event.date} author={event.author} icon={getEventIcon(event.type)} iconColor={getEventColor(event.type)} isLast={index === filteredEvents.length - 1} eventType={event.type} taskMeta={event.taskMeta} data-id="element-4811" data-mp="1am5da5l9ifxu" data-mpi={index} />)}
          </ol>}
      </div>
    </Card>;
}